import * as C from '../common/constants';
import * as ROUTES from '../common/routes';
import Button from './Button';
import CircularProgress from '@mui/material/CircularProgress';
import ContentLayout from './ContentLayout';
import React, { useEffect, useState } from 'react';
import SurveyAccordion from './SurveyAccordion';
import ThemeAnswersPreview from './ThemeAnswersPreview';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { fetchData, clearData } from '../common/app/actions';
import { getText, getPath } from '../common/utils/helpers';
import { makeStyles, createStyles, StyleRules } from '@mui/styles';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslations } from '../common/hooks/useTranslations';

const urlPrefix = getPath();

const useStyles = makeStyles(
  (theme): StyleRules =>
    createStyles({
      centerContainerStyle: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '30vh',
        flex: 1,
      },
      surveyTitleStyle: {
        margin: '12px 0 24px 0',
        fontWeight: FONT_WEIGHT.medium,
        fontSize: '20px',
        fontFamily: FONTS.alegreya,
        color: COLORS.appBlue,
      },
      totalItemsStyle: {
        marginLeft: '6px',
        color: COLORS.textGray,
      },
      surveysContainerStyle: {
        display: 'flex',
        flexDirection: 'row',
        marginBottom: '20px',
      },
      leftSideStyle: {
        width: '190px',
        minWidth: '190px',
        maxWidth: '190px',
        paddingRight: '30px',
        display: 'flex',
        flexDirection: 'column',
      },
      imageContainerStyle: {
        borderRadius: '8px',
        width: '100%',
        height: '120px',
        overflow: 'hidden',
      },
      imageStyle: {
        width: '100%',
        height: 'auto',
        objectFit: 'cover',
      },
      buttonStyle: {
        width: '100%',
        background: COLORS.appBlue,
        // border: `1px solid ${COLORS.appButtonsGray}`,
        marginTop: '10px',
        // color: COLORS.socialCircleGray,
        padding: 0,
      },
      [`@media (max-width: 640px)`]: {
        surveysContainerStyle: {
          flexDirection: 'column',
        },
        leftSideStyle: {
          padding: '0 0 20px 0',
        },
      },
    })
);

const ThemeSurveys = (): JSX.Element => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const lng = useSelector((state: any) => state.app.lng);
  const { search, pathname } = useLocation();
  const { themeId, surveyId } = useParams();

  const addLabel = useTranslations('add_story');

  const themeData = useSelector((state: any) => state.app.themeData);
  const answersPreviewData = useSelector(
    (state: any) => state.app.answersPreviewData
  );

  useEffect(() => {
    dispatch(clearData(C.THEME_DATA));
    dispatch(clearData(C.ANSWERS_PREVIEW_DATA));

    dispatch(fetchData(C.THEME_DATA, { currentThemeId: themeId }));
    dispatch(
      fetchData(C.ANSWERS_PREVIEW_DATA, {
        answersThemeId: themeId,
        dynamicimages: true,
        page: 1,
      })
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!themeData || !answersPreviewData) {
    return (
      <div className={classes.centerContainerStyle}>
        <CircularProgress />
      </div>
    );
  }

  const currentSurveyData = themeData.pages.filter(
    (item) => item.content.uid === surveyId
  );
  const data =
    surveyId && currentSurveyData?.length ? currentSurveyData : themeData.pages;

  const isSingleSubTheme = data?.length === 1;

  const themeName: string = getText(themeData?.content, 'title', lng);
  const themeLabel: string = `Tēma ${themeName?.toUpperCase()}`;
  const rawUri = themeData?.content.uri;
  const uri = rawUri.replace('temas/', '/theme/');

  const getContent = (item, currentSubThemeData) => {
    const subThemeId = item?.content?.uid;
    const img = item?.images?.[0].url;
    const imageUrl: string = `${urlPrefix}${img}`;

    const onClick = (event: React.SyntheticEvent) => {
      event.stopPropagation();
      event.preventDefault();

      navigate({
        pathname: `${ROUTES.ADD}/${themeId}/${subThemeId}`,
        search,
      });
    };

    return (
      <div className={classes.surveysContainerStyle}>
        <div className={classes.leftSideStyle}>
          <div className={classes.imageContainerStyle}>
            <img src={imageUrl} alt="" className={classes.imageStyle} />
          </div>

          <Button
            text={addLabel}
            onClick={onClick}
            className={classes.buttonStyle}
          />
        </div>

        <ThemeAnswersPreview
          data={currentSubThemeData}
          defaultImage={imageUrl}
          isSingleTheme={isSingleSubTheme}
        />
      </div>
    );
  };

  const getThemeSurveys = (item, index) => {
    const surveyTitle: string = getText(item?.content, 'title', lng);

    const subThemeId = item?.content?.uid;
    const currentSubThemeData = answersPreviewData.pages.find(
      (item) => item.content.uid === subThemeId
    );

    const totalItems = currentSubThemeData?.pages?.length;
    const totalItemsLabel = totalItems ? `(${totalItems})` : '';
    const linkData = {
      title: totalItemsLabel,
      link: `${ROUTES.THEME}/${themeId}/${subThemeId}`,
    };

    if (isSingleSubTheme) {
      return (
        <div key={index}>
          <div className={classes.surveyTitleStyle}>
            {surveyTitle}
            {totalItemsLabel && (
              <span className={classes.totalItemsStyle}>{totalItemsLabel}</span>
            )}
          </div>
          {getContent(item, currentSubThemeData)}
        </div>
      );
    }

    return (
      <SurveyAccordion
        key={index}
        title={surveyTitle}
        linkData={linkData}
        expanded
      >
        {getContent(item, currentSubThemeData)}
      </SurveyAccordion>
    );
  };

  return (
    <ContentLayout themeTitle={themeLabel} uri={uri}>
      {data.map(getThemeSurveys)}
    </ContentLayout>
  );
};

export default ThemeSurveys;
